<template>
  <div>
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
      <h4 class="text-sm font-semibold">Comparison analysis</h4>
      <div class="flex items-center gap-2">
        <label class="text-xs text-gray-600">Mode</label>
        <select v-model="surveyCompareModeProxy" @change="actions.onSurveyCompareModeChange()" class="border rounded px-2 py-1 text-xs">
          <option value="ceremony">Ceremony vs ceremony</option>
          <option value="cohort">Cohort vs cohort</option>
        </select>
        <label class="text-xs text-gray-600 ml-2">Metric</label>
        <select v-model="surveyCompareMetricProxy" class="border rounded px-2 py-1 text-xs">
          <option value="response_rate_pct">Response rate %</option>
          <option value="question_completion_rate_pct">Question completion %</option>
          <option value="responders">Responders</option>
          <option value="total_registrations">Registrations</option>
        </select>
        <label class="text-xs text-gray-600 ml-2">Sort</label>
        <select v-model="surveyCompareSortModeProxy" class="border rounded px-2 py-1 text-xs">
          <option value="metric_desc">Metric (high to low)</option>
          <option value="metric_asc">Metric (low to high)</option>
          <option value="delta_desc">Delta vs overall (high to low)</option>
          <option value="date_desc">Date (newest first)</option>
          <option value="date_asc">Date (oldest first)</option>
        </select>
      </div>
    </div>

    <div class="text-xs text-gray-500 mb-3">
      {{ state.surveyCompareMode === 'ceremony' ? 'Ceremony vs ceremony is scoped to the selected season.' : 'Cohort vs cohort compares across all seasons for this university.' }}
    </div>

    <div v-if="state.surveyComparisonLoading" class="text-sm text-gray-500 mb-4">Loading comparison data...</div>
    <div v-else-if="state.surveyComparisonError" class="text-sm text-red-600 mb-4">{{ state.surveyComparisonError }}</div>
    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div class="rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white border">
          <div class="text-gray-500 text-xs">Comparison baseline registrations</div>
          <div class="text-2xl font-semibold">{{ state.surveyComparisonBaseline.total_registrations || 0 }}</div>
        </div>
        <div class="rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white border">
          <div class="text-gray-500 text-xs">Comparison baseline responders</div>
          <div class="text-2xl font-semibold">{{ state.surveyComparisonBaseline.responders || 0 }}</div>
        </div>
        <div class="rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white border">
          <div class="text-gray-500 text-xs">Baseline {{ state.surveyCompareMetricLabel.toLowerCase() }}</div>
          <div class="text-2xl font-semibold">{{ actions.surveyComparisonMetricDisplay(state.surveyComparisonBaseline) }}</div>
        </div>
      </div>

      <div v-if="!state.surveyComparisonRowsSorted.length" class="text-sm text-gray-500">No comparison rows available.</div>
      <template v-else>
        <BarComparisonChart
          :title="`Visual comparison (${state.surveyCompareMetricLabel})`"
          :rows="state.surveyComparisonRowsSorted"
          :label-fn="actions.surveyComparisonRowChartLabel"
          :value-fn="actions.surveyComparisonMetricValue"
          :value-suffix="state.surveyComparisonMetricSuffix"
          :max-value-override="['response_rate_pct', 'question_completion_rate_pct'].includes(state.surveyCompareMetric) ? 100 : null"
          :row-limit.sync="surveyComparisonChartLimitProxy"
          :orientation.sync="surveyComparisonChartOrientationProxy"
          :max-height-px="320"
        />

        <AccordionTable :collapsed.sync="surveyComparisonRowsCollapsedProxy" :title="state.surveyCompareMode === 'ceremony' ? 'Ceremony' : 'Cohort'">
          <template #head>
            <template v-if="state.surveyCompareMode === 'ceremony'">
              <th class="text-right px-3 py-2">Registrations</th>
              <th class="text-right px-3 py-2">Responders</th>
              <th class="text-right px-3 py-2">{{ state.surveyCompareMetricLabel }}</th>
              <th class="text-right px-3 py-2">Delta vs overall</th>
            </template>
            <template v-else>
              <th class="text-right px-3 py-2">Ceremonies</th>
              <th class="text-right px-3 py-2">Registrations</th>
              <th class="text-right px-3 py-2">Responders</th>
              <th class="text-right px-3 py-2">{{ state.surveyCompareMetricLabel }}</th>
              <th class="text-right px-3 py-2">Delta vs overall</th>
            </template>
          </template>
          <tr v-for="row in state.surveyComparisonRowsSorted" :key="`compare-${state.surveyCompareMode}-${row.compare_key}`" class="border-t">
            <template v-if="state.surveyCompareMode === 'ceremony'">
              <td class="px-3 py-2">
                <div class="font-medium">{{ actions.ceremonyTitle(row) }}</div>
                <div class="text-xs text-gray-500">{{ row.ceremony_id }}</div>
              </td>
              <td class="px-3 py-2 text-right">{{ row.total_registrations }}</td>
              <td class="px-3 py-2 text-right">{{ row.responders }}</td>
              <td class="px-3 py-2 text-right">{{ actions.surveyComparisonMetricDisplay(row) }}</td>
              <td class="px-3 py-2 text-right" :class="actions.surveyDeltaClass(actions.surveyComparisonDeltaValue(row))">{{ actions.formatSurveyDelta(actions.surveyComparisonDeltaValue(row)) }}</td>
            </template>
            <template v-else>
              <td class="px-3 py-2">
                <div class="font-medium">{{ row.label }}</div>
                <div class="text-xs text-gray-500">{{ row.compare_key }}</div>
              </td>
              <td class="px-3 py-2 text-right">{{ row.ceremony_count }}</td>
              <td class="px-3 py-2 text-right">{{ row.total_registrations }}</td>
              <td class="px-3 py-2 text-right">{{ row.responders }}</td>
              <td class="px-3 py-2 text-right">{{ actions.surveyComparisonMetricDisplay(row) }}</td>
              <td class="px-3 py-2 text-right" :class="actions.surveyDeltaClass(actions.surveyComparisonDeltaValue(row))">{{ actions.formatSurveyDelta(actions.surveyComparisonDeltaValue(row)) }}</td>
            </template>
          </tr>
        </AccordionTable>

        <div class="border rounded-lg bg-white p-4 mt-5">
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div class="text-sm font-semibold">Selected question comparison</div>
              <div v-if="state.selectedSurveyCompareQuestion" class="text-xs text-gray-600">{{ state.selectedSurveyCompareQuestion.question_title || state.selectedSurveyCompareQuestion.question_key }}</div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <label class="text-xs text-gray-600">Question</label>
              <select v-model="surveyCompareQuestionKeyProxy" @change="actions.onSurveyCompareQuestionChange()" class="border rounded px-2 py-1 text-xs max-w-[260px]">
                <option v-for="q in state.surveyComparisonQuestions" :key="`compare-question-${q.question_key}`" :value="q.question_key">{{ q.question_title || q.question_key }}</option>
              </select>
              <label class="text-xs text-gray-600">Metric</label>
              <select v-model="surveyQuestionCompareMetricProxy" class="border rounded px-2 py-1 text-xs">
                <option value="answer_rate_pct">Answer rate %</option>
                <option value="answered_count">Answered</option>
                <option value="eligible_count">Eligible</option>
              </select>
              <label class="text-xs text-gray-600 ml-2">Sort</label>
              <select v-model="surveyQuestionCompareSortModeProxy" class="border rounded px-2 py-1 text-xs">
                <option value="metric_desc">Metric (high to low)</option>
                <option value="metric_asc">Metric (low to high)</option>
                <option value="delta_desc">Delta vs question overall (high to low)</option>
                <option value="date_desc">Date (newest first)</option>
                <option value="date_asc">Date (oldest first)</option>
              </select>
            </div>
          </div>

          <div v-if="!state.selectedSurveyCompareQuestion" class="text-sm text-gray-500">No question available for comparison in this scope.</div>
          <div v-else-if="state.surveyQuestionCompareLoading" class="text-sm text-gray-500">Updating question comparison...</div>
          <div v-else-if="state.surveyQuestionCompareError" class="text-sm text-red-600">{{ state.surveyQuestionCompareError }}</div>
          <div v-else-if="!state.surveyQuestionComparisonRowsSorted.length" class="text-sm text-gray-500">No comparison rows available for this question.</div>

          <BarComparisonChart
            v-if="state.surveyQuestionComparisonRowsSorted.length"
            :title="`Visual comparison (${state.surveyQuestionCompareMetricLabel})`"
            :rows="state.surveyQuestionComparisonRowsSorted"
            :label-fn="actions.surveyComparisonRowChartLabel"
            :value-fn="actions.surveyQuestionComparisonMetricValue"
            :value-suffix="state.surveyQuestionCompareMetric === 'answer_rate_pct' ? '%' : ''"
            :max-value-override="state.surveyQuestionCompareMetric === 'answer_rate_pct' ? 100 : null"
            :row-limit.sync="surveyQuestionComparisonChartLimitProxy"
            :orientation.sync="surveyQuestionComparisonChartOrientationProxy"
            :max-height-px="220"
          />

          <AccordionTable
            v-if="state.surveyQuestionComparisonRowsSorted.length"
            :collapsed.sync="surveyQuestionComparisonRowsCollapsedProxy"
            :title="state.surveyCompareMode === 'ceremony' ? 'Ceremony' : 'Cohort'"
          >
            <template #head>
              <template v-if="state.surveyCompareMode === 'ceremony'">
                <th class="text-right px-3 py-2">Eligible</th>
                <th class="text-right px-3 py-2">Answered</th>
                <th class="text-right px-3 py-2">Answer rate</th>
                <th class="text-right px-3 py-2">Delta vs question overall</th>
              </template>
              <template v-else>
                <th class="text-right px-3 py-2">Ceremonies</th>
                <th class="text-right px-3 py-2">Eligible</th>
                <th class="text-right px-3 py-2">Answered</th>
                <th class="text-right px-3 py-2">Answer rate</th>
                <th class="text-right px-3 py-2">Delta vs question overall</th>
              </template>
            </template>
            <tr v-for="row in state.surveyQuestionComparisonRowsSorted" :key="`question-compare-${state.surveyCompareMode}-${row.compare_key}`" class="border-t">
              <template v-if="state.surveyCompareMode === 'ceremony'">
                <td class="px-3 py-2">
                  <div class="font-medium">{{ actions.ceremonyTitle(row) }}</div>
                  <div class="text-xs text-gray-500">{{ row.ceremony_id }}</div>
                </td>
                <td class="px-3 py-2 text-right">{{ row.eligible_count }}</td>
                <td class="px-3 py-2 text-right">{{ row.answered_count }}</td>
                <td class="px-3 py-2 text-right">{{ row.answer_rate_pct }}%</td>
                <td class="px-3 py-2 text-right" :class="actions.surveyDeltaClass(row.delta_answer_rate_pct)">{{ actions.formatSurveyDelta(row.delta_answer_rate_pct) }}</td>
              </template>
              <template v-else>
                <td class="px-3 py-2">
                  <div class="font-medium">{{ row.label }}</div>
                  <div class="text-xs text-gray-500">{{ row.compare_key }}</div>
                </td>
                <td class="px-3 py-2 text-right">{{ row.ceremony_count }}</td>
                <td class="px-3 py-2 text-right">{{ row.eligible_count }}</td>
                <td class="px-3 py-2 text-right">{{ row.answered_count }}</td>
                <td class="px-3 py-2 text-right">{{ row.answer_rate_pct }}%</td>
                <td class="px-3 py-2 text-right" :class="actions.surveyDeltaClass(row.delta_answer_rate_pct)">{{ actions.formatSurveyDelta(row.delta_answer_rate_pct) }}</td>
              </template>
            </tr>
          </AccordionTable>

          <div v-if="state.isSurveyCompareOptionQuestion && state.surveyQuestionComparisonRowsSorted.length" class="mt-5 pt-4 border-t">
            <div class="text-xs font-semibold text-gray-700 mb-3">Answer option distribution by ceremony</div>
            <OptionDistributionChart
              v-if="state.surveyQuestionOptionLegend.length"
              title="Answer distribution (all options)"
              :rows="state.surveyQuestionComparisonRowsSorted"
              :options="state.surveyQuestionOptionLegend"
              :label-fn="actions.surveyComparisonRowChartLabel"
              :row-limit.sync="surveyOptionChartLimitProxy"
              :orientation.sync="surveyOptionChartOrientationProxy"
              :row-limit-options="[20, 50, 0]"
              :max-height-px="280"
            />
            <div v-else class="text-xs text-gray-500">No option values available for this question.</div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import AccordionTable from '../AccordionTable.vue'
import BarComparisonChart from '../BarComparisonChart.vue'
import OptionDistributionChart from '../OptionDistributionChart.vue'

export default {
  name: 'SurveyCompareSection',
  components: {
    AccordionTable,
    BarComparisonChart,
    OptionDistributionChart,
  },
  props: {
    state: { type: Object, required: true },
    actions: { type: Object, required: true },
  },
  computed: {
    surveyCompareModeProxy: {
      get() { return this.state.surveyCompareMode },
      set(value) { this.$emit('update:surveyCompareMode', value) },
    },
    surveyCompareMetricProxy: {
      get() { return this.state.surveyCompareMetric },
      set(value) { this.$emit('update:surveyCompareMetric', value) },
    },
    surveyCompareSortModeProxy: {
      get() { return this.state.surveyCompareSortMode },
      set(value) { this.$emit('update:surveyCompareSortMode', value) },
    },
    surveyComparisonRowsCollapsedProxy: {
      get() { return this.state.surveyComparisonRowsCollapsed },
      set(value) { this.$emit('update:surveyComparisonRowsCollapsed', value) },
    },
    surveyCompareQuestionKeyProxy: {
      get() { return this.state.surveyCompareQuestionKey },
      set(value) { this.$emit('update:surveyCompareQuestionKey', value) },
    },
    surveyQuestionCompareMetricProxy: {
      get() { return this.state.surveyQuestionCompareMetric },
      set(value) { this.$emit('update:surveyQuestionCompareMetric', value) },
    },
    surveyQuestionCompareSortModeProxy: {
      get() { return this.state.surveyQuestionCompareSortMode },
      set(value) { this.$emit('update:surveyQuestionCompareSortMode', value) },
    },
    surveyComparisonChartLimitProxy: {
      get() { return this.state.surveyComparisonChartLimit },
      set(value) { this.$emit('update:surveyComparisonChartLimit', value) },
    },
    surveyComparisonChartOrientationProxy: {
      get() { return this.state.surveyComparisonChartOrientation },
      set(value) { this.$emit('update:surveyComparisonChartOrientation', value) },
    },
    surveyQuestionComparisonChartLimitProxy: {
      get() { return this.state.surveyQuestionComparisonChartLimit },
      set(value) { this.$emit('update:surveyQuestionComparisonChartLimit', value) },
    },
    surveyQuestionComparisonChartOrientationProxy: {
      get() { return this.state.surveyQuestionComparisonChartOrientation },
      set(value) { this.$emit('update:surveyQuestionComparisonChartOrientation', value) },
    },
    surveyQuestionComparisonRowsCollapsedProxy: {
      get() { return this.state.surveyQuestionComparisonRowsCollapsed },
      set(value) { this.$emit('update:surveyQuestionComparisonRowsCollapsed', value) },
    },
    surveyOptionChartLimitProxy: {
      get() { return this.state.surveyOptionChartLimit },
      set(value) { this.$emit('update:surveyOptionChartLimit', value) },
    },
    surveyOptionChartOrientationProxy: {
      get() { return this.state.surveyOptionChartOrientation },
      set(value) { this.$emit('update:surveyOptionChartOrientation', value) },
    },
  },
}
</script>
